// UCPWO-418 — die Auftrags-ID in „Projekt bearbeiten“, Bereich Versicherung.
//
// Das Feld selbst ist nicht bearbeitbar: Der Anzeigewert gilt für alle Subprojekte desselben
// Auftrags (Kopien eingeschlossen), das passt nicht in das Formular, das nur dieses eine
// Subprojekt speichert. Wer `project.project`/`update` hat, öffnet per Klick einen Dialog,
// der zeigt, welche Subprojekte mit umbenannt werden, und über eine eigene Mutation speichert.
// Die Auftrags-ID selbst ändert sich nie — an die Schnittstelle geht weiter sie.
import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { AiFillEdit } from 'react-icons/ai';
import {
    Alert,
    AlertIcon,
    Button,
    FormControl,
    FormErrorMessage,
    FormHelperText,
    FormLabel,
    GridItem,
    HStack,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    List,
    ListItem,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
} from '@chakra-ui/react';
import { ListSubprojectsDocument } from '~/gql/ucpw/graphql';
import { usePermission } from '~/hooks';
import {
    isSameOrderLabel,
    orderNumberFromMetadata,
    ORDER_NUMBER_DISPLAY_MAX_LENGTH,
} from '~/meta/data/px-connect';
import { subproject as subprojectGql } from '~/pages/projects/meta/data/projects.gql';
import { join } from '~/utils';

type Subproject = {
    id: number;
    lfdNr?: number | null;
    schadenNr?: string | null;
    metadata?: unknown;
    projekt?: { lfdNr?: number | null } | null;
    projekttyp?: { name?: string | null } | null;
};

export function OrderNumberField({
    projectId,
    subproject,
}: {
    projectId?: number;
    subproject?: Subproject | null;
}) {
    const order = orderNumberFromMetadata(subproject?.metadata);
    const { canEdit } = usePermission('project.project');
    const { isOpen, onOpen, onClose } = useDisclosure();

    // Von Hand angelegte und alte Subprojekte haben keine Auftrags-ID.
    if (!order || !subproject) return null;

    return (
        <GridItem colSpan={1}>
            <FormControl>
                <FormLabel htmlFor="orderNumberDisplay">Auftrags-ID</FormLabel>
                <InputGroup>
                    <Input
                        id="orderNumberDisplay"
                        data-test-id="order-number-field"
                        value={order.shortLabel}
                        title={order.label}
                        isReadOnly
                        cursor={canEdit ? 'pointer' : 'default'}
                        onClick={canEdit ? onOpen : undefined}
                    />
                    {canEdit && (
                        <InputRightElement>
                            <IconButton
                                size="sm"
                                variant="ghost"
                                aria-label="Auftrags-ID umbenennen"
                                data-test-id="order-number-edit"
                                icon={<AiFillEdit />}
                                onClick={onOpen}
                            />
                        </InputRightElement>
                    )}
                </InputGroup>
                {order.label !== order.orderNumber && (
                    <FormHelperText>
                        Auftrags-ID der Schnittstelle: {order.orderNumber}
                    </FormHelperText>
                )}
            </FormControl>
            {isOpen && (
                <OrderNumberDialog
                    projectId={projectId}
                    subproject={subproject}
                    order={order}
                    onClose={onClose}
                />
            )}
        </GridItem>
    );
}

function OrderNumberDialog({
    projectId,
    subproject,
    order,
    onClose,
}: {
    projectId?: number;
    subproject: Subproject;
    order: { orderNumber: string; label: string };
    onClose: () => void;
}) {
    // Ein Wert von vor der Höchstlänge wird gekürzt vorgeschlagen: Das Feld nimmt ohnehin nur
    // 20 Zeichen, und mit Speichern ist der alte Wert bereinigt.
    const [value, setValue] = React.useState(order.label.slice(0, ORDER_NUMBER_DISPLAY_MAX_LENGTH));
    const [serverError, setServerError] = React.useState<string>();

    const { data, loading } = useQuery(ListSubprojectsDocument, {
        context: { clientName: 'ucpw' },
        variables: { filter: { projektId: projectId as number } },
        skip: !projectId,
    });
    const [save, { loading: saving }] = useMutation(subprojectGql.updateOrderNumberDisplay, {
        context: { clientName: 'ucpw' },
        refetchQueries: ['projectDetails', 'listSubprojects'],
        awaitRefetchQueries: true,
    });

    // Dieselbe Auswahl wie in der API (order-number.ts): gleiche Schadennummer, gleiche ID.
    const damageNumber = subproject.schadenNr?.trim() ?? '';
    const sameDamage = ((data?.items?.items ?? []) as Subproject[]).filter(
        (sp) => (sp?.schadenNr?.trim() ?? '') === damageNumber
    );
    const affected = sameDamage.filter(
        (sp) => orderNumberFromMetadata(sp.metadata)?.orderNumber === order.orderNumber
    );
    const conflict = sameDamage
        .map((sp) => orderNumberFromMetadata(sp.metadata))
        .find(
            (other) =>
                other &&
                other.orderNumber !== order.orderNumber &&
                isSameOrderLabel(other.label, value)
        );

    const trimmed = value.trim();
    const localError = !trimmed
        ? 'Der Anzeigewert darf nicht leer sein.'
        : trimmed.length > ORDER_NUMBER_DISPLAY_MAX_LENGTH
        ? `Der Anzeigewert darf höchstens ${ORDER_NUMBER_DISPLAY_MAX_LENGTH} Zeichen lang sein.`
        : conflict
        ? `„${trimmed}“ ist bei dieser Schadennummer schon für Auftrag ${conflict.orderNumber} vergeben.`
        : undefined;

    const onSave = async () => {
        setServerError(undefined);
        try {
            const response = await save({
                variables: { subprojektId: subproject.id, orderNumberDisplay: trimmed },
            });
            const error = response.data?.app?.item?.error;
            if (error?.message) {
                setServerError(error.message);
                return;
            }
            onClose();
        } catch (error) {
            setServerError((error as Error)?.message || 'Speichern fehlgeschlagen.');
        }
    };

    return (
        <Modal isOpen onClose={onClose}>
            <ModalOverlay />
            <ModalContent rounded="none" maxWidth="container.sm" data-test-id="order-number-dialog">
                <ModalHeader p={5} borderBottomWidth={1} borderColor="gray.200">
                    Auftrags-ID umbenennen
                </ModalHeader>
                <ModalBody p={5}>
                    <Text mb={4} color="gray.600">
                        Auftrag {order.orderNumber} zur Schadennummer {damageNumber}. Der neue Name
                        wird nur angezeigt; an die Schnittstelle geht weiter die Auftrags-ID{' '}
                        {order.orderNumber}.
                    </Text>
                    <FormControl isInvalid={Boolean(localError)} isRequired>
                        <FormLabel htmlFor="orderNumberDisplayInput">Anzeigewert</FormLabel>
                        <Input
                            id="orderNumberDisplayInput"
                            data-test-id="order-number-input"
                            value={value}
                            maxLength={ORDER_NUMBER_DISPLAY_MAX_LENGTH}
                            autoFocus
                            onChange={(event) => setValue(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' && !localError && !saving) onSave();
                            }}
                        />
                        {!localError && (
                            <FormHelperText>
                                Eine kurze Kennung wie „02“, höchstens{' '}
                                {ORDER_NUMBER_DISPLAY_MAX_LENGTH} Zeichen ({trimmed.length}/
                                {ORDER_NUMBER_DISPLAY_MAX_LENGTH}).
                            </FormHelperText>
                        )}
                        <FormErrorMessage>{localError}</FormErrorMessage>
                    </FormControl>
                    <Text mt={5} mb={2} fontWeight="semibold">
                        Wird geändert bei
                    </Text>
                    {loading ? (
                        <Text color="gray.500">wird geladen …</Text>
                    ) : (
                        <List spacing={1} data-test-id="order-number-affected">
                            {affected.map((sp) => (
                                <ListItem key={sp.id}>
                                    <HStack spacing={3} alignItems="baseline">
                                        <Text minW="5rem" flexShrink={0}>
                                            {join([sp.projekt?.lfdNr, sp.lfdNr], '-')}
                                        </Text>
                                        <Text flex={1} minW={0} color="gray.600" noOfLines={1}>
                                            {sp.projekttyp?.name}
                                        </Text>
                                        <Text
                                            color="gray.500"
                                            textAlign="right"
                                            minW={0}
                                            wordBreak="break-all"
                                        >
                                            {orderNumberFromMetadata(sp.metadata)?.shortLabel} →{' '}
                                            <Text as="span" color="gray.800" fontWeight="semibold">
                                                {trimmed || '…'}
                                            </Text>
                                        </Text>
                                    </HStack>
                                </ListItem>
                            ))}
                        </List>
                    )}
                    {serverError && (
                        <Alert status="error" mt={5}>
                            <AlertIcon />
                            {serverError}
                        </Alert>
                    )}
                </ModalBody>
                <ModalFooter p={5} borderTopWidth={1} borderColor="gray.200">
                    <HStack>
                        <Button size="sm" onClick={onClose}>
                            Abbrechen
                        </Button>
                        <Button
                            size="sm"
                            colorScheme="blue"
                            data-test-id="order-number-save"
                            onClick={onSave}
                            isLoading={saving}
                            isDisabled={Boolean(localError) || saving || trimmed === order.label}
                        >
                            Speichern
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
